import moneyLottie from "@/assets/lottie/money.lottie?url";
import { callFirebaseFunction } from "@/bridge/callFirebaseFunction";
// import {
//   CheckoutDialog,
//   type CheckoutDialogProps,
// } from "@/components/CheckoutDialog/CheckoutDialog";
// import { DialogOverlay } from "@/components/CheckoutDialog/DialogOverlay";
import { ErrorOverlay } from "@/components/ErrorOverlay";
import { FramedGameGrid } from "@/components/Game/FramedGameGrid";
import { Game } from "@/components/Game/Game";
import type { Coordinates, SquareInfo } from "@/components/Game/GameGrid/GameGrid";
// import { LoadingOverlay } from "@/components/LoadingOverlay";
// import { ThemedPage } from "@/components/ThemedPage/ThemedPage";
import { retry } from "@/helpers/retry";
import { safeAreaPadding } from "@/helpers/safeArea";
import { useElementRect } from "@/hooks/useElementRect";
import { useHapticFeedback } from "@/hooks/useHapticFeedback";
import { useSoundEffects } from "@/hooks/useSoundEffects";
import { gameThemes } from "@/themes/themes";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { Color } from "@skillscratch/shared-constants";
import type {
  GameActionEndScratchArgs,
  GameActionEndScratchRet,
  GameActionRevealSquareArgs,
  GameActionRevealSquareRet,
  GameActionStartArgs,
  GameActionStartRet,
  GameThemeId,
} from "@skillscratch/shared-types";
import { centsToPrettyDollars, getTreasureDistance } from "@skillscratch/shared-util";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import type { UserGameAction } from '@skillscratch/shared-types';
import { useFirebaseFunction } from "@/hooks/useFirebaseFunction";
// import { ManagedPurchaseDialog } from "@/components/PurchaseDialog/ManagedPurchaseDialog";
import { GameEndDialog } from "@/components/Game/GameEndDialog";
import { GridContext } from "@/context/GridContext";
import { useGrid } from "@/hooks/useGrid";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { useModal } from "@/hooks/useModal";
import { PurchaseModal } from "@/components/Modals/PurchaseModal";
import { storeListings } from "@/helpers/storeListings";
import { getStoreById } from "@/helpers/storeListings";
import { standardRewards } from "@/helpers/storeListings";
import { useViewTransition } from "@/hooks/useViewTransition";
import { TutorialModal } from "@/components/Modals/TutorialModal";
// import { LoadingComponent } from "@/components/LoadingComponent";


// Define types for our API calls
type GetUserPrivateInfoRequest = {
  fields?: string[];
};

type GetUserPrivateInfoResponse = {
  userInfo: {
    playedFirstGame?: boolean;
  };
};


export type GetGameActionsArgs = {
  gamePackId: string;
  gameSessionId: string;
}

export type GetGameActionsResponse = {
  actions: UserGameAction[];
}

export const TREASURES_TO_WIN = 6;

export type WinDialogInfo = GameActionEndScratchRet["colorBar"] &
  Pick<Required<GameActionEndScratchRet>, "baseReward">;

  export function GamePage() {
    const location = useLocation();
    const gameThemeId = location.state.gameThemeId as GameThemeId;
    const gamePackId = location.state.gamePackId as string;
    const startGameRet = location.state.startGameRet as GameActionStartRet;
    const cardCount = Number(location.state.cardCount);
    const nextTutorialPage = location.state.nextTutorialPage as
      | string
      | undefined;
    
    const [userPrivateData, userPrivateDataIsLoading] = useFirebaseFunction<
        GetUserPrivateInfoRequest, 
        GetUserPrivateInfoResponse
      >(
        'getUserPrivateInfo',
        (fn) => fn({ fields: ["playedFirstGame"] }),
        []
      );
    
    const { openModal } = useModal();

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
      if (userPrivateData?.userInfo.playedFirstGame || userPrivateDataIsLoading) {
        return;
      }
      // @ts-expect-error fbq api
      fbq('track', 'Purchase', {
        value: 1.00,
        currency: 'USD'
      });
      openModal(<TutorialModal />);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userPrivateData]);


  
    return (
      <FunctionalGamePage
        key={startGameRet.gameSessionId}
        gameThemeId={gameThemeId}
        cardCount={cardCount}
        gamePackId={gamePackId}
        startGameRet={startGameRet}
        nextTutorialPage={nextTutorialPage}
      />
    );
  }
  
  function FunctionalGamePage({
    gameThemeId,
    gamePackId,
    cardCount,
    startGameRet,
    nextTutorialPage,
  }: {
    gameThemeId: GameThemeId;
    gamePackId: string;
    cardCount: number;
    startGameRet: GameActionStartRet;
    nextTutorialPage?: string | undefined;
  }) {
    // Initialize grid state with empty grid
    const initialGrid = useMemo(() => generateEmptyGrid(startGameRet.gridSize), [startGameRet.gridSize]);
    const [grid, setGrid] = useState(initialGrid);
  
    // Provide the grid state through context
    return (
      <GridContext.Provider value={{ grid, setGrid, gamePackId, gameSessionId: startGameRet.gameSessionId, treasureCount: startGameRet.treasureCount }}>
        <GamePageContent
          gameThemeId={gameThemeId}
          gamePackId={gamePackId}
          cardCount={cardCount}
          startGameRet={startGameRet}
          nextTutorialPage={nextTutorialPage}
        />
      </GridContext.Provider>
    );
  }
  
  function GamePageContent({
    gameThemeId,
    gamePackId,
    cardCount,
    startGameRet,
    nextTutorialPage,
  }: {
    gameThemeId: GameThemeId;
    gamePackId: string;
    cardCount: number;
    startGameRet: GameActionStartRet;
    nextTutorialPage?: string | undefined;
  }) {
    const gameTheme = gameThemes[gameThemeId as keyof typeof gameThemes];
    const { navigateNormal } = useViewTransition(); 

    const listing = getStoreById(gameTheme.id);
    console.log('[gameThemeId]', gameTheme.id);
    console.log('[listing]', listing);
    const pricePerCard = (listing?.prices[0].price ?? 0) / (listing?.prices[0].totalCards ?? 1);
    const minimumReward = standardRewards['6'] * (pricePerCard as number / 100);

    const roundedMinimumReward = Math.round(minimumReward / 100) * 100;
    
    
    // Access grid state from context
    const { grid, setGrid } = useGrid();
  
    const [gameError, setGameError] = useState<Error>();
    const [gameEndReason, setGameEndReason] = useState<
      "no-scratch" | "no-time" | "found-all-treasures"
    >();
    const [gameGridRect, setGameGridRect] = useState<DOMRect>();
    
    // Track whether the first square has been revealed
    const [hasFirstReveal, setHasFirstReveal] = useState(false);
    
    // Prefetched next game data
    const [nextGameData, setNextGameData] = useState<GameActionStartRet | null>(null);
    // const [isNextGameLoading, setIsNextGameLoading] = useState(false);
    const [isPrefetchingNextGame, setIsPrefetchingNextGame] = useState(false);
  
    // const [allGameActions, gameActionsLoading, ,] = useFirebaseFunction<GetGameActionsArgs, GetGameActionsResponse>(
    //   "getGameActions",
    //   (callable) => callable({
    //     gamePackId,
    //     gameSessionId: startGameRet.gameSessionId,
    //   }),
    //   [],
    // );
  
    // Check if there are any existing reveal actions in loaded game actions
    // useEffect(() => {
    //   if (!hasFirstReveal && allGameActions?.actions) {
    //     // If there's at least one revealSquare action, consider the first reveal done
    //     const hasRevealActions = allGameActions.actions.some(action => action.type === "revealSquare");
    //     if (hasRevealActions) {
    //       setHasFirstReveal(true);
    //     }
    //   }
    // }, [allGameActions, hasFirstReveal]);
  
    // Prefetch next game after first square reveal and when more cards remain
    useEffect(() => {
      // Only start prefetching after the first square has been revealed
      if (!hasFirstReveal) {
        return;
      }
      
      // Don't prefetch if we're on the last card, in tutorial mode, or already prefetching/fetched
      if (cardCount <= 1 || 
          nextTutorialPage !== undefined || 
          nextGameData !== null || 
          isPrefetchingNextGame) {
        return;
      }
  
      // Only prefetch if the current game is loaded
      setIsPrefetchingNextGame(true);
      
      callFirebaseFunction<GameActionStartArgs, GameActionStartRet>(
        "gameAction",
        {
          type: "start",
          gamePackId,
        }
      )
        .then((result) => {
          setNextGameData(result);
        })
        .catch(() => {
          // Silently handle prefetch errors - we'll try again when needed
          // console.warn("Failed to prefetch next game:", error);
          // should probably show an error/handle with sentry/posthog
          setIsPrefetchingNextGame(false);
        })
        .finally(() => {
          setIsPrefetchingNextGame(false);
        });

    }, [cardCount, gamePackId, nextTutorialPage, nextGameData, isPrefetchingNextGame, hasFirstReveal]);
  
    // useEffect(() => {
    //   for (const action of allGameActions?.actions ?? []) {
    //     if (action.type === "revealSquare") {
    //       const { x, y } = action.coordinates;
    //       setGrid((grid) => {
    //         const newGrid = [...grid];
    //         newGrid[y][x] = action.info;
  
    //         return newGrid;
    //       });
    //     }
    //   }
    // }, [allGameActions, setGrid]);
  
    const foundTreasureCount = useMemo(
      () =>
        grid.flat().filter((s) => s.type === "treasure" && !s.disabled).length,
      [grid]
    );
  
    const [winDialogInfo, setWinDialogInfo] = useState<WinDialogInfo>();
    // const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
    // const [checkoutDialogInfo, setCheckoutDialogInfo] =
    //   useState<Omit<CheckoutDialogProps, "onDismiss">>();
  
    const [gameEndModalRef, gameEndModalRect] = useElementRect<HTMLDivElement>();
    const [gameEndModalGameGridRef, gameEndModalGameGridRect] =
      useElementRect<HTMLDivElement>();
    const [gameEndModalDialogRef, gameEndModalDialogRect] =
      useElementRect<HTMLDivElement>();
  
    const playSoundEffect = useSoundEffects();
    const playHapticFeedback = useHapticFeedback();
  
    const treasureFoundCount = useRef(0);

    const { openModal } = useModal();
  
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    const onNextGameClick = useCallback(() => {
      // console.log('[cardCount]', cardCount);
      // if (cardCount <= 1) {

      // }

      // if (isPrefetchingNextGame) {
      //   return;
      // }
  
      // If we have prefetched data, use it immediately
      if (nextGameData) {
        navigateNormal("/game", {
          replace: true,
          viewTransition: true,
          state: {
            gamePackId: gamePackId,
            cardCount: cardCount - 1,
            gameThemeId: gameThemeId,
            startGameRet: nextGameData,
          },
        });
        return;
      }

      const listing = getStoreById(gameThemeId);
      const shouldSkip = !listing || 'showInStore' in listing

      const skippedListing = shouldSkip ? storeListings[0] : listing;
      const skippedId = shouldSkip ? storeListings[0].id : gameThemeId;

      const fallbackPrices = storeListings[0].prices;
      const fallbackId = skippedListing?.prices ? skippedId : storeListings[0].id;
      
      openModal(<PurchaseModal id={fallbackId as GameThemeId} prices={skippedListing?.prices ?? fallbackPrices}
                type={'refill'} />);


  
      // Otherwise, fetch the next game (fallback path)
      // setIsNextGameLoading(true);
  
      // callFirebaseFunction<GameActionStartArgs, GameActionStartRet>(
      //   "gameAction",
      //   {
      //     type: "start",
      //     gamePackId,
      //   }
      // )
      //   .then((result) => {
      //     navigateNormal("/game", {
      //       replace: true,
      //       viewTransition: true,
      //       state: {
      //         gamePackId: gamePackId,
      //         cardCount: cardCount - 1,
      //         gameThemeId: gameThemeId,
      //         startGameRet: result,
      //       },
      //     });
      //   })
      //   .catch((error) => {
      //     if (error.message === "No more game sessions left.") {
      //       // setShowPurchaseDialog(true);
      //       return;
      //     }
      //     setGameError(error);
      //   })
      //   .finally(() => {
      //     setIsNextGameLoading(false);
      //   });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gamePackId, cardCount, nextGameData, gameThemeId, isPrefetchingNextGame]);
  
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    const onGoHomeClick = useCallback(() => {
      navigateNormal('/index', {
        replace: true,
        viewTransition: true,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  
    const onSquareReveal = useCallback(
      (coordinates: Coordinates) => {
        // if (gameActionsLoading) {
        //   return;
        // }
    
        playSoundEffect?.("scratchShort");
        playHapticFeedback?.({ style: "soft" });
    
        setGrid((grid) => {
          const newGrid = [...grid];
          newGrid[coordinates.y][coordinates.x] = { type: "loading" };
          return newGrid;
        });
    
        // Record start time for measuring server response time
        const startTime = performance.now();
        
        retry(() =>
          callFirebaseFunction<
            GameActionRevealSquareArgs,
            GameActionRevealSquareRet
          >("gameAction", {
            type: "revealSquare",
            coordinates: coordinates,
            gamePackId,
            gameSessionId: startGameRet.gameSessionId,
          })
        )
          .then((result) => {
            // Calculate and capture response time in milliseconds
            const endTime = performance.now();
            const responseTime = endTime - startTime;
            
            // Capture the timing event in PostHog
            posthog.capture("scratch_time", {
              duration_ms: responseTime,
              coordinates: coordinates,
              gamePackId,
              gameSessionId: startGameRet.gameSessionId
            });
            
            if (result.info.type === "treasure") {
              treasureFoundCount.current++;
    
              const treasureFoundCountUpTo13 = Math.min(
                treasureFoundCount.current,
                13
              ) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13;
    
              playSoundEffect?.(`treasure${treasureFoundCountUpTo13}`);
    
              playHapticFeedback?.({ style: "heavy" });
            }
    
            setGrid((grid) => {
              const newGrid = [...grid];
              newGrid[coordinates.y][coordinates.x] = result.info;
              return newGrid;
            });
    
            // Mark that the first reveal has happened
            if (!hasFirstReveal) {
              setHasFirstReveal(true);
            }
          })
          .catch((error) => {
            // Capture the error in Sentry
            Sentry.captureException(error, {
              extra: { 
                function: "onSquareReveal", 
                coordinates,
                gamePackId,
                gameSessionId: startGameRet.gameSessionId,
                type: "scratch_error"
              },
            });
            
            // Also log to PostHog for analytics correlation
            posthog.capture("scratch_error", {
              error_message: error.message,
              error_code: error.code,
              coordinates,
              gamePackId,
              gameSessionId: startGameRet.gameSessionId
            });
            
            setGameError(error);
          });
      },
      [gamePackId, hasFirstReveal, playHapticFeedback, playSoundEffect, setGrid, startGameRet.gameSessionId]
    );
    
    const onGameEnd = useCallback(
      async (reason: "no-scratch" | "no-time" | "found-all-treasures") => {
        setGameEndReason(reason);
  
        await retry(() =>
          callFirebaseFunction<GameActionEndScratchArgs, GameActionEndScratchRet>(
            "gameAction",
            {
              type: "endScratch",
              gamePackId,
              gameSessionId: startGameRet.gameSessionId,
            }
          )
        )
          .then((result) => {
            const colorBar = result.colorBar;
            const baseReward = result.baseReward;
            if (colorBar && baseReward) {
              setWinDialogInfo({
                ...colorBar,
                baseReward,
              });
            }
  
            if (!result.treasureLocations.length) {
              return;
            }
  
            setGrid((grid) => {
              const newGrid = [...grid];
  
              for (let x = 0; x < startGameRet.gridSize; x++) {
                for (let y = 0; y < startGameRet.gridSize; y++) {
                  if (newGrid[y][x].type === "hidden") {
                    const treasureLocations = result.treasureLocations;
                    const treasureDistance = getTreasureDistance(
                      { x, y },
                      treasureLocations
                    );
                    const isTreasure = treasureLocations.some(
                      (loc) => loc.x === x && loc.y === y
                    );
  
                    newGrid[y][x] = {
                      type: isTreasure ? "treasure" : "empty",
                      distance: treasureDistance,
                      disabled: true,
                    };
                  }
                }
              }
  
              return newGrid;
            });
          })
          .catch((error) => {
            setGameError(error);
            // should probably show an error/handle with sentry/posthog
          });
      },
      [gamePackId, startGameRet.gameSessionId, startGameRet.gridSize, setGrid]
    );
    
    const onGridRectChange = useCallback((rect: DOMRect) => {
      setGameGridRect(rect);
    }, []);
  
    // if (gameActionsLoading) {
    //   return <LoadingComponent />;
    // }
  
    return (
    <div className="flex flex-col h-screen overflow-y-scroll font-bold"
      style={{ backgroundColor: gameTheme.colorScheme.tileColors[1].background }}>
      
        {gameTheme && (
          <Game
            grid={grid}
            colorScheme={gameTheme.colorScheme}
            scratchLimit={startGameRet.scratchLimit}
            treasureCount={startGameRet.treasureCount}
            timeLimit={startGameRet.timeLimit}
            maxRewards={startGameRet.maxRewards}
            nameImage={gameTheme.nameImage}
            background={`url("${gameTheme.background}")`}
            hideGrid={!!gameEndReason}
            onSquareReveal={onSquareReveal}
            onGameEnd={onGameEnd}
            onGridRectChange={onGridRectChange}
            cardName={gameTheme.id}
          />
        )}
  
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2,
  
            transitionProperty: "backdrop-filter, visibility, pointer-events",
            transitionDuration: "200ms",
            transitionTimingFunction: "linear",
            transitionBehavior: "allow-discrete",
  
            willChange: "backdrop-filter, visibility, pointer-events",
  
            backdropFilter: gameEndReason ? "blur(7px)" : undefined,
  
            visibility: gameEndReason ? "visible" : "hidden",
            pointerEvents: gameEndReason ? undefined : "none",
          }}
          aria-hidden={!gameEndReason}
        >
          {(gameEndReason === "found-all-treasures" ||
            foundTreasureCount >= TREASURES_TO_WIN) && (
            <DotLottieReact
              src={moneyLottie}
              dotLottieRefCallback={(ref) => {
                ref?.addEventListener("load", () => {
                  ref?.setLayout({ fit: "cover", align: [0.5, 0.5] });
                });
              }}
              renderConfig={{
                autoResize: true,
              }}
              style={{
                position: "absolute",
                inset: "0",
              }}
              speed={0.5}
              segment={[65, 130]}
              autoplay
              loop
            />
          )}
  
          <div
            style={{
              maxHeight: "calc(var(--real-vh, 1vh) * 100)",
              paddingTop: safeAreaPadding("4vw", "top"),
              paddingBottom: safeAreaPadding("bottom"),
              paddingLeft: safeAreaPadding("left"),
              paddingRight: safeAreaPadding("right"),
            }}
          >
            {/* we need a separate element so we can get the height - safe padding */}
            <div
              ref={gameEndModalRef}
              style={{
                height: "100%",
                width: "100%",
                maxHeight: `calc(calc(var(--real-vh, 1vh) * 100) - ${safeAreaPadding(
                  "4vw",
                  "top"
                )} - ${safeAreaPadding("bottom")})`,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  margin: "0 auto",
                  maxWidth: "95vw",
                  width: "100%",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <div
                  ref={gameEndModalGameGridRef}
                  style={{
                    position: "relative",
  
                    maxWidth: "95vw",
  
                    height: gameEndReason
                      ? gameEndModalRect &&
                        gameEndModalDialogRect &&
                        Math.min(
                          gameEndModalRect.height - gameEndModalDialogRect.height,
                          gameEndModalDialogRect.width / 0.85
                        )
                      : "auto",
                    width: gameEndReason
                      ? "auto"
                      : gameGridRect?.width,
  
                    transform: gameEndReason
                      ? ""
                      : `translateY(${
                          gameGridRect &&
                          gameEndModalGameGridRect &&
                          gameGridRect.y - gameEndModalGameGridRect.y
                        }px) scale(${
                          gameGridRect &&
                          gameEndModalGameGridRect &&
                          gameGridRect.width / gameEndModalGameGridRect.width
                        })`,
  
                    aspectRatio: "0.85",
  
                    transitionProperty: "width, height, transform",
                    transitionDuration: "200ms",
                    transitionTimingFunction: "ease-in-out",
  
                    willChange: "width, height, transform",
                  }}
                >
                  <FramedGameGrid
                    scratchable={false}
                    grid={grid}
                    colorScheme={gameTheme.colorScheme}
                    bottomBar={
                      <div
                        style={{
                          background:
                            gameEndReason === "found-all-treasures" ||
                            foundTreasureCount >= TREASURES_TO_WIN
                              ? Color.gold
                              : gameTheme.colorScheme.controlColor,
                          color:
                            gameEndReason === "found-all-treasures" ||
                            foundTreasureCount >= TREASURES_TO_WIN
                              ? "#fff"
                              : gameTheme.colorScheme.onControlColor,
                          overflow: "hidden",
  
                          height: "100%",
  
                          display: "grid",
                          placeItems: "center",
  
                          fontSize: "7cqw",
                        }}
                      >
                        {gameEndReason === "found-all-treasures" ||
                        foundTreasureCount >= TREASURES_TO_WIN
                          ? `Scratched ${foundTreasureCount} treasures!`
                          : gameEndReason === "no-time"
                          ? "You ran out of time"
                          : `${6 - treasureFoundCount.current} away from ${centsToPrettyDollars(roundedMinimumReward, 'round')} win`}
                      </div>
                    }
                  />
                </div>
              </div>
              <div
                ref={gameEndModalDialogRef}
                style={{
                  paddingTop: "2.5vw",
                }}
              >
                <GameEndDialog
                  winDialogInfo={winDialogInfo}
                  gameEndReason={gameEndReason}
                  foundTreasureCount={foundTreasureCount}
                  gameTheme={gameTheme}
                  gamePackId={gamePackId}
                  gameSessionId={startGameRet.gameSessionId}
                  isTutorial={nextTutorialPage !== undefined}
                  onNextGameClick={onNextGameClick}
                  onGoHomeClick={onGoHomeClick}
                  onGameError={setGameError}
                />
              </div>
            </div>
          </div>
        </div>
  
        {/* <LoadingOverlay
          show={isNextGameLoading}
          style={{
            zIndex: 3,
          }}
          fullScreen
        /> */}
        
        <ErrorOverlay
          errorMessage={gameError?.message}
          buttonText="Close"
          onButtonClick={() => {
            navigateNormal('/index', {
              replace: false,
              viewTransition: true,
            });
          }}
          style={{
            zIndex: 4,
          }}
        />
  
        {/* <DialogOverlay
          show={showPurchaseDialog}
          onDismiss={() => setShowPurchaseDialog(false)}
          style={{
            zIndex: 5,
          }}
        >
          {showPurchaseDialog && (
            <ManagedPurchaseDialog
              type="refill"
              storeListingId={gamePackId}
              onPackNotFound={() => {
                // @ts-expect-error numbers are not typed in navigate
                navigate(-1, {
                  replace: false,
                  viewTransition: true,
                });
              }}
            />
          )}
        </DialogOverlay> */}
  
        {/* <DialogOverlay
          show={checkoutDialogInfo !== undefined}
          onDismiss={() => setCheckoutDialogInfo(undefined)}
          style={{
            zIndex: 6,
          }}
        >
          {checkoutDialogInfo && (
            <CheckoutDialog
              {...checkoutDialogInfo}
              onDismiss={(success) => {
                if (success) {
                  onNextGameClick();
                }
  
                setCheckoutDialogInfo(undefined);
              }}
            />
          )}
        </DialogOverlay> */}
    </div>
    );
  }
  
  function generateEmptyGrid(gridSize: number): SquareInfo[][] {
    return Array.from({ length: gridSize }, () =>
      Array.from(
        { length: gridSize },
        () =>
          ({
            type: "hidden",
          } satisfies SquareInfo)
      )
    );
  }